// The search field (combobox) with its states and its list of results, shared by the header flyout
// and the results page: one rendering, one keyboard behaviour.
import { Button, Input, Spinner, __css, tokens, useId } from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import { useEffect, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchView } from './api';
import { type SearchEntry, type SearchScope } from './model';
import { ResultsList } from './results-list';
import { useListNavigation } from './use-list-navigation';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","qttt6h":"fl4vidq"},"field":{"Bns2gvi":"f1c209jx"},"hint":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","jrapky":0,"Frg6f3":0,"t21cq0":0,"B6of3ja":0,"B74szlk":"f1s184ao","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f16epzkf","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","sj55zd":"fkfq4zb"},"state":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r","Bt984gj":"f6jr5hl","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f16epzkf","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","sj55zd":"fkfq4zb"},"stateTitle":{"jrapky":0,"Frg6f3":0,"t21cq0":0,"B6of3ja":0,"B74szlk":"f1s184ao","Bhrd7zp":"fl43uef","sj55zd":"f19n0e5","Btd35i7":"fjksvth"},"stateBody":{"jrapky":0,"Frg6f3":0,"t21cq0":0,"B6of3ja":0,"B74szlk":"f1s184ao"},"more":{"jrapky":0,"Frg6f3":0,"t21cq0":0,"B6of3ja":0,"B74szlk":"f1s184ao","Dbcxam":0,"rjzwhg":0,"Bblux5w":"fnllxr2","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","sj55zd":"fkfq4zb"},"results":{"Bmxbyg5":"f5zp4f","qttt6h":"fl4vidq"},"srOnly":{"qhf8xq":"f1euv43f","Bns2gvi":"fittdk8","st59p4":"f156r0dy","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","Bgl5zvf":"f1wch0ki","Huce71":"fz5stix"}});

export interface SearchPanelProps {
  orgId: string;
  input: string;
  onInputChange: (value: string) => void;
  scope: SearchScope;
  /** Results per kind requested from the API. */
  limit: number;
  /** Hits shown per kind. */
  perGroup: number;
  /** Flyout: "see more" lines. Page: the "refine your search" note instead. */
  seeMore: boolean;
  /** A result was chosen (Enter or click). */
  onOpen: (entry: SearchEntry) => void;
  /** Enter with nothing highlighted. */
  onSubmit: (q: string) => void;
  /** Gives the field the focus when it appears. */
  focusOnMount?: boolean;
  /** Visual hint at the end of the field (the keyboard shortcut). */
  hint?: ReactElement;
  /** Placed between the field and the results (the page's kind chips). */
  between?: ReactNode;
  fieldLabel: string;
  placeholder?: string;
}

export function SearchPanel({
  orgId,
  input,
  onInputChange,
  scope,
  limit,
  perGroup,
  seeMore,
  onOpen,
  onSubmit,
  focusOnMount = false,
  hint,
  between,
  fieldLabel,
  placeholder,
}: SearchPanelProps) {
  const { t } = useTranslation('search');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const idPrefix = useId('bf-search');
  const listboxId = `${idPrefix}-list`;
  const inputId = `${idPrefix}-input`;

  const view = useSearchView({ orgId, input, scope, limit, idPrefix, perGroup, seeMore });
  const nav = useListNavigation({
    entries: view.list.entries,
    onOpen,
    onSubmit: () => {
      onSubmit(input.trim());
    },
  });

  useEffect(() => {
    if (!focusOnMount) return;
    const focus = () => document.getElementById(inputId)?.focus();
    focus();
    // The dialog moves the focus to its own surface right after it opens: take it back.
    const timer = setTimeout(focus, 0);
    return () => {
      clearTimeout(timer);
    };
  }, [focusOnMount, inputId]);

  const hasList = view.state === 'results';
  const liveText = view.busy
    ? ''
    : view.state === 'results'
      ? t('results.count', { count: view.list.count })
      : view.state === 'empty'
        ? t('states.emptyTitle', { q: view.q })
        : view.state === 'error'
          ? view.rateLimited
            ? t('states.rateLimited')
            : t('states.failed')
          : '';

  let body: ReactNode = null;
  if (view.state === 'prompt' || view.state === 'tooShort')
    body = (
      <p className={styles.hint}>
        <Search20Regular aria-hidden="true" />
        {t(view.state === 'prompt' ? 'states.prompt' : 'states.tooShort')}
      </p>
    );
  else if (view.state === 'loading')
    body = (
      <div className={styles.hint}>
        <Spinner size="tiny" label={t('states.loading')} labelPosition="after" />
      </div>
    );
  else if (view.state === 'error')
    body = (
      <div className={styles.state}>
        <p className={styles.stateTitle}>
          {view.rateLimited ? t('states.rateLimited') : t('states.failed')}
        </p>
        <Button size="small" onClick={view.retry}>
          {tCommon('actions.retry')}
        </Button>
      </div>
    );
  else if (view.state === 'empty')
    body = (
      <div className={styles.state}>
        <p className={styles.stateTitle} dir="auto">
          {t('states.emptyTitle', { q: view.q })}
        </p>
        <p className={styles.stateBody}>{t('states.emptyBody')}</p>
      </div>
    );
  else
    body = (
      <>
        <ResultsList
          model={view.list}
          idPrefix={idPrefix}
          listboxId={listboxId}
          activeId={nav.activeId}
          q={view.q}
          onHover={nav.setActiveId}
          onOpen={onOpen}
          className={styles.results}
        />
        {!seeMore && view.hasMore ? <p className={styles.more}>{t('page.hasMore')}</p> : null}
      </>
    );

  return (
    <div className={styles.root}>
      <Input
        id={inputId}
        className={styles.field}
        size="large"
        role="combobox"
        aria-label={fieldLabel}
        aria-expanded={hasList}
        aria-controls={hasList ? listboxId : undefined}
        aria-activedescendant={hasList ? nav.activeId : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        spellCheck={false}
        maxLength={100}
        placeholder={placeholder}
        value={input}
        contentBefore={<Search20Regular aria-hidden="true" />}
        {...(hint ? { contentAfter: hint } : {})}
        onChange={(_event, data) => {
          onInputChange(data.value);
        }}
        onKeyDown={nav.onKeyDown}
      />
      {between}
      <div role="status" aria-live="polite" className={styles.srOnly}>
        {liveText}
      </div>
      {body}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/search/search-panel.griffel.css";